/*
  getRecaptchaEnterpriseToken retrieves a token from the Google
  Recaptcha Enterprise API. The token is typically added to
  the params of a form and sent to the back-end where it is
  assessed via another API call to Recaptcha Enterprise. The backend
  then decides whether the user is human or a bot based on the token.

  By default, the action encoded in the token is 'PROFILE_CONTACT_UPDATE'.

  If VUE_APP_RECAPTCHA_SITE_KEY is undefined, or if
  VUE_APP_RECAPTCHA_ENABLED is not set to 'true', the function
  returns null.
*/

function resolveRecaptchaConfiguration(configurationEnv = {}) {
  const configuredEnabledValue = configurationEnv.VUE_APP_RECAPTCHA_ENABLED;
  const configuredSiteKey = configurationEnv.VUE_APP_RECAPTCHA_SITE_KEY;

  const recaptchaEnabled =
    configuredEnabledValue === undefined || configuredEnabledValue === null
      ? import.meta.env.VUE_APP_RECAPTCHA_ENABLED === 'true'
      : String(configuredEnabledValue).toLowerCase() === 'true';

  const siteKey =
    configuredSiteKey === undefined || configuredSiteKey === null
      ? import.meta.env.VUE_APP_RECAPTCHA_SITE_KEY
      : configuredSiteKey;

  return { recaptchaEnabled, siteKey };
}

export function parentShouldUseRecaptchaEnterpriseToken(config = {}) {
  if (config.enable_recaptcha === false) {
    return false;
  }

  const { recaptchaEnabled } = resolveRecaptchaConfiguration(config.env || {});

  return recaptchaEnabled;
}

export async function getRecaptchaEnterpriseToken(
  action = 'PROFILE_CONTACT_UPDATE',
  configurationEnv = {},
) {
  const { recaptchaEnabled, siteKey } = resolveRecaptchaConfiguration(configurationEnv);

  if (!recaptchaEnabled) {
    return null;
  }

  if (!siteKey) {
    return null;
  }

  const scriptSource = 'https://www.google.com/recaptcha/enterprise.js?render=' + siteKey;

  const loadRecaptcha = () => {
    return new Promise((resolve, _reject) => {
      const appendRecaptchaScript = () => {
        const recaptchaScript = document.createElement('script');

        recaptchaScript.async = true;
        recaptchaScript.defer = true;
        recaptchaScript.onload = () => {
          recaptchaScript.dataset.loaded = 'true';
          resolve();
        };
        recaptchaScript.onerror = resolve;

        recaptchaScript.setAttribute('src', scriptSource);

        recaptchaScript.type = 'text/javascript';
        document.head.appendChild(recaptchaScript);
      };

      const existingScript = document.querySelector(`script[src="${scriptSource}"]`);

      if (existingScript) {
        if (window.grecaptcha?.enterprise) {
          resolve();
          return;
        }

        const existingScriptLoaded =
          existingScript.dataset.loaded === 'true' || existingScript.readyState === 'complete';

        if (existingScriptLoaded) {
          existingScript.remove();
          appendRecaptchaScript();
          return;
        }

        existingScript.addEventListener('load', resolve, { once: true });
        existingScript.addEventListener('error', resolve, { once: true });
        return;
      }

      appendRecaptchaScript();
    });
  };

  await loadRecaptcha();

  if (!window.grecaptcha?.enterprise) {
    return null;
  }

  const waitForReady = new Promise((resolve, _reject) => {
    window.grecaptcha.enterprise.ready(resolve);
  });

  await waitForReady;

  const token = await window.grecaptcha.enterprise.execute(siteKey, {
    action,
  });

  return token;
}
